/**
 * Language code to name and flag mapping.
 * Used across the app for displaying language names with flags.
 */

export const LANGUAGES = {
  // Tier 1: CIS priority
  en: { name: 'English', flag: '🇬🇧' },
  uk: { name: 'Ukrainian', flag: '🇺🇦' },
  ru: { name: 'Russian', flag: '🇷🇺' },

  // Tier 2: Global major
  zh: { name: 'Chinese', flag: '🇨🇳' },
  'zh-Hans': { name: 'Chinese (Simplified)', flag: '🇨🇳' },
  'zh-Hant': { name: 'Chinese (Traditional)', flag: '🇹🇼' },
  es: { name: 'Spanish', flag: '🇪🇸' },
  ar: { name: 'Arabic', flag: '🇸🇦' },
  hi: { name: 'Hindi', flag: '🇮🇳' },
  pt: { name: 'Portuguese', flag: '🇵🇹' },
  'pt-BR': { name: 'Portuguese (Brazil)', flag: '🇧🇷' },
  'pt-PT': { name: 'Portuguese (Portugal)', flag: '🇵🇹' },
  fr: { name: 'French', flag: '🇫🇷' },
  de: { name: 'German', flag: '🇩🇪' },
  ja: { name: 'Japanese', flag: '🇯🇵' },
  ko: { name: 'Korean', flag: '🇰🇷' },

  // Tier 3: European
  it: { name: 'Italian', flag: '🇮🇹' },
  pl: { name: 'Polish', flag: '🇵🇱' },
  tr: { name: 'Turkish', flag: '🇹🇷' },
  nl: { name: 'Dutch', flag: '🇳🇱' },
  sv: { name: 'Swedish', flag: '🇸🇪' },
  cs: { name: 'Czech', flag: '🇨🇿' },
  ro: { name: 'Romanian', flag: '🇷🇴' },
  el: { name: 'Greek', flag: '🇬🇷' },
  hu: { name: 'Hungarian', flag: '🇭🇺' },
  bg: { name: 'Bulgarian', flag: '🇧🇬' },
  sr: { name: 'Serbian', flag: '🇷🇸' },
  lt: { name: 'Lithuanian', flag: '🇱🇹' },

  // Tier 4: Other
  id: { name: 'Indonesian', flag: '🇮🇩' },
  vi: { name: 'Vietnamese', flag: '🇻🇳' },
  th: { name: 'Thai', flag: '🇹🇭' },
  sk: { name: 'Slovak', flag: '🇸🇰' },
  da: { name: 'Danish', flag: '🇩🇰' },
  fi: { name: 'Finnish', flag: '🇫🇮' },
  no: { name: 'Norwegian', flag: '🇳🇴' },
  he: { name: 'Hebrew', flag: '🇮🇱' },
  sw: { name: 'Swahili', flag: '🇰🇪' },
  ta: { name: 'Tamil', flag: '🇮🇳' },

  // Additional languages (for detection fallback)
  fa: { name: 'Persian', flag: '🇮🇷' },
  bn: { name: 'Bengali', flag: '🇧🇩' },
  ms: { name: 'Malay', flag: '🇲🇾' },
  tl: { name: 'Filipino', flag: '🇵🇭' },
  te: { name: 'Telugu', flag: '🇮🇳' },
  mr: { name: 'Marathi', flag: '🇮🇳' },
  ur: { name: 'Urdu', flag: '🇵🇰' },
  ka: { name: 'Georgian', flag: '🇬🇪' },
  hy: { name: 'Armenian', flag: '🇦🇲' },
  az: { name: 'Azerbaijani', flag: '🇦🇿' },
  kk: { name: 'Kazakh', flag: '🇰🇿' },
  uz: { name: 'Uzbek', flag: '🇺🇿' },
  lv: { name: 'Latvian', flag: '🇱🇻' },
  et: { name: 'Estonian', flag: '🇪🇪' },
  hr: { name: 'Croatian', flag: '🇭🇷' },
  sl: { name: 'Slovenian', flag: '🇸🇮' },
  mk: { name: 'Macedonian', flag: '🇲🇰' },
  sq: { name: 'Albanian', flag: '🇦🇱' },
  is: { name: 'Icelandic', flag: '🇮🇸' },
  mt: { name: 'Maltese', flag: '🇲🇹' },
  cy: { name: 'Welsh', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
  ga: { name: 'Irish', flag: '🇮🇪' },
  af: { name: 'Afrikaans', flag: '🇿🇦' },
  ne: { name: 'Nepali', flag: '🇳🇵' },
  si: { name: 'Sinhala', flag: '🇱🇰' },
  km: { name: 'Khmer', flag: '🇰🇭' },
  lo: { name: 'Lao', flag: '🇱🇦' },
  my: { name: 'Burmese', flag: '🇲🇲' },
  mn: { name: 'Mongolian', flag: '🇲🇳' },
};

/**
 * The codes that appear in the "translate to" dropdown. Single source of
 * truth - both upload screens (DocumentUpload, ScanUpload) import this.
 * When adding a language, this is the place to update (and the i18n
 * `chat.json` files and the backend `LanguageCode` literal).
 */
export const TARGET_LANGUAGE_CODES = [
  'en', 'uk', 'ru', 'zh', 'es', 'ar', 'hi', 'pt', 'fr', 'de', 'ja', 'ko',
  'it', 'pl', 'tr', 'nl', 'sv', 'cs', 'ro', 'el', 'hu', 'bg', 'sr', 'lt', 'lv', 'et',
  'id', 'vi', 'th', 'sk', 'da', 'fi', 'no', 'he', 'sw', 'kk', 'ta',
];

/**
 * Languages that have regional variants.
 * When user selects one of these, a second dropdown appears.
 */
export const LANGUAGE_VARIANTS = {
  pt: [
    { code: 'pt-BR', nameKey: 'pt-BR' },
    { code: 'pt-PT', nameKey: 'pt-PT' },
  ],
  zh: [
    { code: 'zh-Hans', nameKey: 'zh-Hans' },
    { code: 'zh-Hant', nameKey: 'zh-Hant' },
  ],
};

/**
 * Get language name by code.
 * Falls back to uppercase code if not found.
 */
export function getLanguageName(code) {
  if (!code) return '';
  return LANGUAGES[code]?.name || code.toUpperCase();
}

/**
 * Get language flag by code.
 * Falls back to globe emoji if not found.
 */
export function getLanguageFlag(code) {
  if (!code) return '🌐';
  return LANGUAGES[code]?.flag || '🌐';
}

/**
 * Get formatted language display with flag and name.
 */
export function getLanguageDisplay(code) {
  if (!code) return '';
  const lang = LANGUAGES[code];
  if (lang) {
    return `${lang.flag} ${lang.name}`;
  }
  return `🌐 ${code.toUpperCase()}`;
}
